import React from 'react';
import { Box, Typography } from '@mui/material';
import PercentileChart from './PercentileChart';

/**
 * Universal component for displaying grades and scores consistently across the application
 * 
 * Design Standards:
 * - GRADE: White letter on colored rectangular box with round corners
 * - SCORE: Circular progress bar with appropriate color
 * - Layout: Score on LEFT, Grade on RIGHT
 * 
 * @param {Object} props
 * @param {string} props.grade - The grade (A, B, C, D, F)
 * @param {number} props.score - The raw score (0-100)
 * @param {boolean} props.showLabels - Whether to show "Grade" and "Score" labels (default: true)
 * @param {string} props.size - Size variant: 'small', 'medium', 'large' (default: 'medium')
 * @param {boolean} props.vertical - Display vertically instead of horizontally (default: false)
 */
const GradeScoreDisplay = ({ 
  grade, 
  score, 
  showLabels = true, 
  size = 'medium',
  vertical = false 
}) => {
  const getGradeColor = (grade) => {
    switch (grade?.toUpperCase()) {
      case 'A':
        return '#4CAF50'; // Green
      case 'B':
        return '#8BC34A'; // Light Green
      case 'C':
        return '#FF9800'; // Orange
      case 'D':
        return '#FF5722'; // Deep Orange
      case 'F':
        return '#F44336'; // Red
      default:
        return '#9E9E9E'; // Grey
    }
  };

  // Size configurations
  const sizeConfig = {
    small: {
      gradeBox: { width: 40, height: 40, borderRadius: 1.5 },
      gradeText: 'h5',
      chartSize: 50,
      labelVariant: 'caption',
      gap: 2
    },
    medium: {
      gradeBox: { width: 60, height: 60, borderRadius: 2 },
      gradeText: 'h4',
      chartSize: 70,
      labelVariant: 'body2',
      gap: 3
    },
    large: {
      gradeBox: { width: 80, height: 80, borderRadius: 2.5 },
      gradeText: 'h3',
      chartSize: 90,
      labelVariant: 'body1',
      gap: 4
    }
  };

  const config = sizeConfig[size] || sizeConfig.medium;

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: config.gap,
        flexDirection: vertical ? 'column' : 'row'
      }}
    >
      {/* Score Display (LEFT or TOP) */}
      <Box sx={{ textAlign: 'center' }}>
        {showLabels && (
          <Typography 
            variant={config.labelVariant} 
            color="text.secondary" 
            sx={{ mb: 0.5, fontWeight: 600, height: '1.5em' }}
          >
            Score
          </Typography>
        )}
        <PercentileChart rawScore={score} size={config.chartSize} showTitle={false} />
      </Box>

      {/* Grade Display (RIGHT or BOTTOM) */}
      <Box sx={{ textAlign: 'center' }}>
        {showLabels && (
          <Typography 
            variant={config.labelVariant} 
            color="text.secondary" 
            sx={{ mb: 0.5, fontWeight: 600, height: '1.5em' }}
          >
            Grade
          </Typography>
        )}
        <Box
          sx={{
            ...config.gradeBox,
            backgroundColor: getGradeColor(grade),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography 
            variant={config.gradeText} 
            fontWeight="bold" 
            color="white"
          >
            {grade || 'N/A'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default GradeScoreDisplay;
